import React, { useCallback } from 'react';
import Modal from '~/components/Modal';
import { MdClose } from 'react-icons/md';
import FormFooter from '~/components/form/form-footer';
import { FormSelect } from '~/components/form';
import { useForm } from '~/hooks';
import { InsuredIndividual } from './InsuredIndividual';
import { InsuredEntity } from './InsuredEntity';
import { buyerSellerRelationship } from '~/api';


/**
 * @typedef AddInsuredValues
 * @property {number}   sectionId
 * @property {string}   titleOpinionId 'Your File Number'
 * @property {object}   insuredPrimary 
 * @property {object}   insuredSecondary 
 * @property {[object]} insuredPrimaryType 
 * @property {[object]} insuredSecondaryType
 * @property {[object]} insuredTypeOptions
 * @property {object}   relationship
 * @property {[object]} relationshipOptions
 * @property {boolean}  addInsuredFlag
 * @property {[object]} data
 * @property {boolean}  editInsured
 * 
 * 
 * @typedef FormState
 * @property {AddInsuredValues} values The current form values
 */

/** @type {AddInsuredValues} */
const defaultState = {
    id: 0,
    sectionId: 0,
    titleOpinionId: 0,
    insuredPrimary: null,
    insuredPrimaryType: { label: '...', value: '' },
    insuredSecondaryType: {label: '...', value: '' },
    insuredSecondary: null,
    insuredTypeOptions: [
        {
            id: 1,
            label: 'Individual',
            value: 'Individual'
        },
        {
            id: 2,
            label: 'Entity',
            value: 'Entity'
        }
    ],
    relationship: { label: '...', value: '' },
    relationshipOptions: [],
    data: [],
    addInsuredModalFLag: false,
    editInsured: false
};

const loadData = async (values, props) => {
    const defaultInsuredType = defaultState.insuredTypeOptions.find(s => s.id == 1);
    const relationshipOptions = (await buyerSellerRelationship.getBuyerSellerRelationships()).map(f => ({
        label: f.Name, value: f.Name
    }));
    let editMode= false;
    if(props.insuredDetailData && Object.keys(props.insuredDetailData).length === 0){
        editMode = true;
    }
    
    return {
        insuredDetailData: props.insuredDetailData,
        ...values,
        ...props,
        relationshipOptions: relationshipOptions,
        sectionId: values ? values.sectionId: 0,
        relationship: (!editMode && props.insuredDetailData) ? props.insuredDetailData._data.relationship : relationshipOptions[0],
        insuredPrimaryType: (!editMode && props.insuredDetailData) ? props.insuredDetailData._data.insuredPrimaryType : defaultInsuredType,
        insuredSecondaryType: (!editMode && props.insuredDetailData) ? props.insuredDetailData._data.insuredSecondaryType :  defaultInsuredType
    };
};


const AddInsured= (props) => {

    const editDetailData = props.editInsured ? props.insuredDetailData._data : defaultState;    
    const onLoad = useCallback((values, props) => {
        return loadData(values, props);
    }, [props.isModalOpen]);


    const { state, fieldActions } = useForm({
        initialState: { ...defaultState },
        props,
        onLoad,
        debug: true
    });     

    return (
        <Modal isModalOpen={props.isModalOpen} preventScroll className='w-3/4 bg-clip-border bg-white rounded-md'>
            <section className='h-full flex flex-col'>
                <header className='pt-3 pb-2 px-8 border-b flex items-center justify-between'>
                    <p className='font-semibold'>{props.headerTxt}</p>
                    <button 
                        className='rounded-full p-2 hover:bg-gray-150 focus:outline-none'
                        onClick={props.onCancel}
                    >
                        <MdClose />
                    </button>
                </header>
                <div className='flex-grow overflow-y-scroll'>
                    <div className="px-8 py-4 grid grid-cols-4 grid-flow-row gap-x-2 gap-y-2 text-sm mb-2 ">
                        <FormSelect
                            label="Insured Type*"
                            type='select'
                            required
                            options={state.values.insuredTypeOptions}
                            isClearable={false}
                            groupClassName="col-span-1"
                            className="w-full border-1 border-gray-200 rounded bg-gray-50"
                            path='insuredPrimaryType'   
                            value={state.values.insuredPrimaryType}
                            error={state.errors.insuredPrimaryType}
                            {...fieldActions}                                        
                            onChange={(insuredPrimaryType) => {
                                fieldActions.onChange(insuredPrimaryType, 'insuredPrimaryType');
                                fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');  
                            }}                             
                        />
                        <div className="col-span-5">
                            {(state.values.insuredPrimaryType.value) == 'Individual' ?
                                <InsuredIndividual 
                                    path='insuredPrimary'
                                    detailData={props.editInsured ? editDetailData.insuredPrimary: null}
                                    onInsuredIndividualChange={(values) => {
                                        fieldActions.onChange(values, 'insuredPrimary');
                                    }}                                            
                                />
                                :
                                <InsuredEntity 
                                    path='insuredPrimary'
                                    detailData={ props.editInsured ? editDetailData.insuredPrimary: null}
                                    onInsuredIndividualChange={(values) => {
                                        fieldActions.onChange(values, 'insuredPrimary');
                                    }}  
                                />
                            }
                        </div>
                        
                        <div className="col-span-6">
                            {(state.values.insuredPrimaryType.value) == 'Individual' ?
                                <FormSelect
                                    label="Vesting*"
                                    type='select'
                                    required
                                    options={state.values.relationshipOptions}
                                    isClearable={false}
                                    isSearchable
                                    groupClassName="col-span-4 mb-6"
                                    className="w-full border-1 border-gray-200 rounded bg-gray-50"
                                    path='relationship'
                                    value={state.values.relationship}
                                    error={state.errors.relationship}
                                    {...fieldActions}
                                    onChange={(relationship) => {
                                        fieldActions.onChange(relationship, 'relationship');
                                        fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                                    }}                         
                                />
                                : <></>
                            }
                        </div>
                        {
                            (state.values.insuredPrimaryType.value) == 'Individual' && state.values.relationship && (state.values.relationship.value.includes('and') || state.values.relationship.value.includes('husband') ||
                            state.values.relationship.value.includes('wife')  ||  state.values.relationship.value.includes('married')  ||   
                            state.values.relationship.value.includes('both')  ||  state.values.relationship.value.includes('partners'))
                                ?
                                <>
                                    <FormSelect
                                        label="Insured Type*"
                                        type='select'
                                        required
                                        options={state.values.insuredTypeOptions}
                                        isClearable={false}
                                        groupClassName="col-span-1"
                                        className="w-full border-1 border-gray-200 rounded bg-gray-50"
                                        path='insuredSecondaryType'   
                                        value={state.values.insuredSecondaryType}
                                        error={state.errors.insuredSecondaryType}
                                        {...fieldActions}
                                        onChange={(insuredSecondaryType) => {
                                            fieldActions.onChange(insuredSecondaryType, 'insuredSecondaryType');
                                            fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');  
                                        }}                             
                                    />

                                    <div className="col-span-5">                                         
                                        {(state.values.insuredSecondaryType.value) == 'Individual' ?
                                            <InsuredIndividual 
                                                path='insuredSecondary'
                                                detailData={ props.editInsured ? editDetailData.insuredSecondary: null}
                                                onInsuredIndividualChange={(values) => {
                                                    fieldActions.onChange(values, 'insuredSecondary');
                                                }}
                                            />
                                            :
                                            <InsuredEntity 
                                                path='insuredSecondary'
                                                detailData={  props.editInsured ? editDetailData.insuredSecondary: null}
                                                onInsuredIndividualChange={(values) => {
                                                    fieldActions.onChange(values, 'insuredSecondary');
                                                }}  
                                            />
                                        }
                                    </div>
                                </>
                                :
                                <div className="col-span-6"></div>
                        }
                        
                        <div className="col-span-6 flex justify-end items-center">                                        
                            <FormFooter
                                
                                cancelBtnTxt={'Cancel'}
                                submitBtnTxt={
                                    props.editInsured ? 'Save' : 'Add'}
                                onSubmit={() => {
                                    props.editInsured ? 
                                        props.saveInsured(props.insuredDetailData, state.values)
                                        :
                                        props.addInsured(state.values);
                                }}
                                onCancel={props.onCancel}
                            />                                    
                        </div>   
                    </div>                 
                </div>
            </section>     
        </Modal>
    );
};

export default AddInsured;

